import React, { useEffect } from "react";
import { useRouter } from "next/compat/router";
import {
  Heading,
  Text,
  Box,
  IconFa,
  Totaliser,
  Loader,
  Button,
} from "@cruk/cruk-react-components";
import { faShareAlt } from "@fortawesome/free-solid-svg-icons";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { fwsUrlPartnerPageSlug } from "@fwa/src/services/partnerService";
import { imagePathFromImageType } from "@fwa/src/services/imageService";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";

import { PartnerPageSuperTeams } from "@fwa/src/components/PartnerPageSuperTeams";
import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";
import { Share } from "@fwa/src/components/Share";
import { Seasonal } from "@fwa/src/components/Seasonal";

import sharedStyles from "@fwa/src/components/styles.module.css";

import type { PartnerPageType, BrandNameType } from "@fwa/src/types";

export function PartnerPage({
  pageData,
  themeName,
}: {
  pageData: PartnerPageType;
  themeName?: BrandNameType;
}) {
  const themeNameOrCruk = themeName || "cruk";
  //  figure out unique page url for page cacheId
  const router = useRouter();
  const { slug } = router?.query || {};
  const slugString = queryAsString(slug);
  const url = slugString.length
    ? `${fwsUrlPartnerPageSlug({ slug: slugString })}`
    : null;

  // page cache
  const opts = { fallbackData: pageData };
  const { data: page, error } = useSwrGet<PartnerPageType>(url, opts);

  const [brandName, setBrandName] = useBrandContext();
  useEffect(() => {
    if (!!themeNameOrCruk && themeNameOrCruk !== brandName) {
      setBrandName(themeNameOrCruk);
    }
  }, [themeNameOrCruk, brandName, setBrandName]);

  const hasTarget = page?.target && page.target > 0;

  return (
    <ErrorPageWrapper error={error!}>
      <div className={sharedStyles["content-max-width"]}>
        {/* Loading state */}
        {!page ? <Loader /> : null}

        {/* Data state */}
        {page ? (
          <>
            {/* Top actions block */}
            <Box backgroundColor="primary" padding="xs" marginBottom="none">
              <div className={sharedStyles["row-stretch"]}>
                <Share
                  page={page}
                  feedItem={null}
                  isOwner={false}
                  popoverPosition="bottom"
                  ctaLocation="header"
                >
                  <Button
                    type="button"
                    appearance="secondary"
                    data-cta-type="open-share"
                  >
                    <IconFa faIcon={faShareAlt} />
                    Share
                  </Button>
                </Share>
              </div>
            </Box>
            <Box margin="none">
              <Seasonal>
                <img
                  className={sharedStyles["header-image"]}
                  src={
                    page.headerImage
                      ? imagePathFromImageType(page.headerImage)
                      : ""
                  }
                  alt=""
                />
              </Seasonal>
            </Box>

            {/* Title block */}
            <Box backgroundColor="primary" padding="s" margin="none">
              <Heading
                h1
                textColor="text-on-primary"
                margin="none"
                overflowWrap="break-word"
                style={{ display: "inline-block" }}
              >
                {page.title}
              </Heading>
            </Box>

            {/* totaliser block */}
            <Box backgroundColor="background-light" padding="s">
              <Totaliser
                isCompact={false}
                target={hasTarget ? page.target : undefined}
                total={page.donationsTotalAmount}
                giftAid={page.donationsGiftAidTotalAmount}
              />
            </Box>

            {/* Subtitle block */}
            {page.subtitle ? (
              <Box backgroundColor="primary" padding="s">
                <Heading
                  h2
                  textSize="xxl"
                  textColor="text-on-primary"
                  marginBottom="none"
                >
                  {page.subtitle}
                </Heading>
              </Box>
            ) : null}

            {/* story block */}
            {page.story ? (
              <Box backgroundColor="background-light" padding="s">
                <Heading h2 textSize="xxl">
                  Our story
                </Heading>
                <Text>{page.story}</Text>
              </Box>
            ) : null}

            {/* members block */}
            <Box
              backgroundColor="background-light"
              padding="s"
              marginBottom="xxl"
            >
              <PartnerPageSuperTeams
                partnerPageId={page.uniqueId}
                itemsPerPage={12}
              />
            </Box>
          </>
        ) : null}
      </div>
    </ErrorPageWrapper>
  );
}

export default PartnerPage;
